import * as api from 'api/api'

import {
    ARTIST_DASHBOARD_MAX_QUESTIONS,
    FORUM,
    WEBSOCKET_FORUM_ENTRY,
    WEBSOCKET_FORUM_UPVOTE,
    WEBSOCKET_FORCE_REFRESH,
    WEBSOCKET_SET_ACTIVE_MUX_ASSET,
    WEBSOCKET_VIEWER_COUNT_UPDATE,
    WEBSOCKET_VIDEO_STATUS_CHANGE,
    WEBSOCKET_REMAINING_PASS_UPDATE,
    WEBSOCKET_INITIATE_PROTOCOL_ARMAGEDDON,
    WEBSOCKET_DEACTIVATE_PROTOCOL_ARMAGEDDON,
    WEBSOCKET_FORUM_TOP_ENTRIES_BATCH,
} from 'constants.js'

import { viewerRegistered } from './viewer'

import {
    updateVideoStatus,
    initiateProtocolArmageddon,
    deactivateProtocolArmageddon,
    updateRemainingPassesAction,
} from './liveShow/thunks'
import { updateActiveMuxStream } from './liveShow'

import WsChannelProvider from 'api/ws'
import _ from 'lodash'
import { createFetcherThunk } from 'redux/thunkCreators'
import { createSelector } from 'reselect'
import { createSlice } from '@reduxjs/toolkit'
import { showErrorBanner } from './messagingBanner'
import { VideoStatus } from 'data/Video'

const wsChannelProvider = new WsChannelProvider()

export const initialState = {
    userCanPost: true,
    errorMessage: null,
    forumEntries: {
        byId: {},
        new: [],
        top: [],
    },
    newUpvotes: {},
    forumEntriesReceived: 0,
    wsChannelName: null,
    isLoading: false,
    forumSortMode: FORUM.FORUM_SORT_TOP,
    forumLiveSync: true,
    forumPage: 1,
    forumUpdateSource: null,
    forumPendingBatchId: {
        new: null,
        top: null,
    },
    forumPendingBatches: {
        new: [],
        top: [],
    },
}

const addNewForumEntries = (newEntries = [], state) => {
    const entries = state.forumEntries[state.forumSortMode]

    for (const entry of newEntries) {
        state.forumEntries.byId[entry._id] = entry
        if (!entries.includes(entry._id)) entries.push(entry._id)
    }

    return state
}

const handleForumBatch = (type, payload, state) => {
    const { forumPendingBatches: batches } = state

    if (state.forumPendingBatchId[type] !== payload.batch.id) {
        state.forumPendingBatchId[type] = payload.batch.id
        batches[type] = []
    }

    batches[type] = [...batches[type], payload]

    if (batches[type].length === payload.batch.total && state.forumLiveSync) {
        const sortedBatches = batches[type].sort((a, b) => a.batch.seq - b.batch.seq)

        state.forumEntries[type] = []

        for (const batch of sortedBatches) {
            for (const entry of batch.data) {
                const newUpvoteId = state.newUpvotes[entry._id]

                if (newUpvoteId && !entry.upvoteUsers.includes(newUpvoteId)) {
                    entry.upvoteUsers.push(newUpvoteId)
                    delete state.newUpvotes[entry._id]
                }
                state.forumEntries[type].push(entry._id)
                state.forumEntries.byId[entry._id] = entry
            }
        }

        batches[type] = []
    }

    return state
}

const interaction = createSlice({
    name: 'interaction',
    initialState,
    reducers: {
        resetState: _ => initialState,
        forumNewestEntriesBatch: (state, { payload }) => handleForumBatch(FORUM.FORUM_SORT_NEW, payload, state),
        forumTopEntriesBatch: (state, { payload }) => handleForumBatch(FORUM.FORUM_SORT_TOP, payload, state),
        forumRequested: state => ({ ...state }),
        forumReceived: (state, { payload }) => {
            state.isLoading = false
            state.forumEntriesReceived = payload.length
            state.forumLiveSync = true
            state.forumUpdateSource = FORUM.UPDATE_SOURCE_FULL_REFRESH
            state.forumEntries.byId = {}
            state.forumEntries.new = []
            state.forumEntries.top = []

            addNewForumEntries(payload, state)
        },
        forumAddPage: (state, { payload }) => {
            addNewForumEntries(payload, state)

            state.forumEntriesReceived = payload.length
            state.isLoading = false
            state.forumLiveSync = false
            state.forumUpdateSource = FORUM.UPDATE_SOURCE_PAGE_REQUEST

            if (payload.length === FORUM.FORUM_ENTRY_PAGE_SIZE) {
                state.forumPage = state.forumPage + 1
            }
        },
        setShowChannelName: (state, { payload }) => ({ ...state, wsChannelName: payload }),
        forumEntrySubmissionRequested: (state, { payload }) => {
            state.isLoading = true

            state.forumEntries.byId[payload.temporaryId] = {
                _id: payload.temporaryId,
                upvoteUsers: [],
                body: payload.body,
                user: payload.user,
            }
            state.forumEntries.new.unshift(payload.temporaryId)
            state.forumEntries.top.push(payload.temporaryId)
        },
        forumEntryCreated: (state, { payload: { entry, temporaryId } }) => {
            state.forumEntries.byId[entry._id] = entry

            state.newUpvotes[entry._id] = state.newUpvotes[temporaryId]
            delete state.newUpvotes[temporaryId]

            state.forumEntries.new.unshift(entry._id)
            state.forumEntries.top.push(entry._id)
        },
        deleteTemporaryEntry: (state, { payload: { temporaryId } }) => {
            delete state.forumEntries.byId[temporaryId]
            state.forumEntries.new = state.forumEntries.new.filter(entryId => entryId !== temporaryId)
            state.forumEntries.top = state.forumEntries.top.filter(entryId => entryId !== temporaryId)
        },
        forumUpVoteRequested: state => ({ ...state, isLoading: true }),
        setForumSort: (state, { payload }) => ({ ...state, forumSortMode: payload }),
        setForumLive: (state, { payload }) => ({ ...state, forumLiveSync: payload }),
        setInteractionError: (state, { payload }) => ({ ...state, isLoading: false, errorMessage: payload }),
        clearInteractionError: state => ({ ...state, errorMessage: null }),
        forumUpVoteSubmitted: (state, { payload }) => {
            state.forumEntries.byId[payload.entryId].upvoteUsers.push(payload.userId)
            state.newUpvotes[payload.entryId] = payload.userId
            state.forumUpdateSource = FORUM.UPDATE_SOURCE_UPVOTE
        },
        forumAcknowledgementSubmitted: (state, { payload }) => {
            state.forumEntries.byId[payload._id] = payload
            state.forumUpdateSource = FORUM.UPDATE_SOURCE_ACK
        },
        reachedMaxAmountOfPosts: state => {
            state.userCanPost = false
        },
    },
})

const { actions, reducer } = interaction // Extract the action creators object and the reducer
export default reducer

//Selectors
const makeEntry = interaction => id => interaction.forumEntries.byId[id]

export const getInteraction = store => store.interaction

const getTopEntries = (interaction, acknowledged = false) =>
    interaction.forumEntries[FORUM.FORUM_SORT_TOP]
        .map(id => interaction.forumEntries.byId[id])
        .sort((a, b) => b.totalUpvotes - a.totalUpvotes)
        .filter(e => (acknowledged ? e.acknowledged : !e.acknowledged))
        .slice(0, ARTIST_DASHBOARD_MAX_QUESTIONS)

export const getTopAcknowledgedEntries = createSelector(getInteraction, state => getTopEntries(state, true))
export const getTopUnacknowledgedEntries = createSelector(getInteraction, state => getTopEntries(state, false))

export const getForum = createSelector(getInteraction, interaction =>
    interaction.forumEntries[interaction.forumSortMode].map(makeEntry(interaction)),
)

export const getShowChannel = createSelector(getInteraction, interaction =>
    wsChannelProvider.getWsChannel(interaction.wsChannelName),
)
export const getInteractionError = createSelector(getInteraction, interaction => interaction.errorMessage)
export const getForumSortMode = ({ interaction }) => interaction.forumSortMode
export const isForumLive = ({ interaction }) => interaction.forumLiveSync
export const forumEntriesReceivedCount = ({ interaction }) => interaction.forumEntriesReceived
export const getCurrentForumPage = ({ interaction }) => interaction.forumPage
export const getLastUpdateSource = ({ interaction }) => interaction.forumUpdateSource

//Action creators
export const {
    resetState,
    forumNewestEntriesBatch,
    forumTopEntriesBatch,
    forumRequested,
    forumReceived,
    forumAddPage,
    setShowChannelName,
    newForumEntry,
    forumTopRankedEntryUpdate,
    forumUpVoteSubmitted,
    forumUpVoteRequested,
    forumEntrySubmissionRequested,
    setForumSort,
    setForumLive,
    setInteractionError,
    clearInteractionError,
    forumAcknowledgementSubmitted,
    reachedMaxAmountOfPosts,
    deleteTemporaryEntry,
    forumEntryCreated,
} = actions

//Thunks
export const retrieveForum = createFetcherThunk(api.fetchForumEntries, forumRequested, forumReceived)

export const retrieveForumPage = showId => async (dispatch, getState) => {
    dispatch(forumRequested())

    const newEntries = await api.fetchForumEntries(
        showId,
        FORUM.FORUM_ENTRY_PAGE_SIZE,
        getForumSortMode(getState()),
        getCurrentForumPage(getState()),
    )

    dispatch(forumAddPage(newEntries))
}

export const upVoteForumEntry = ({ entryId, userId }) => async (dispatch, getState) => {
    dispatch(forumUpVoteRequested())
    dispatch(forumUpVoteSubmitted({ entryId, userId }))

    getShowChannel(getState()).trigger(WEBSOCKET_FORUM_UPVOTE, await api.patchUpVote(entryId))
}

export const acknowledgeForumEntry = entry => async dispatch => {
    const updatedEntry = entry.acknowledged
        ? api.patchForumUnacknowledged(entry._id)
        : api.patchForumAcknowledged(entry._id)

    dispatch(forumAcknowledgementSubmitted(await updatedEntry))
}

export const createForumEntry = (body, user) => async (dispatch, getState) => {
    const temporaryId = Date.now()

    dispatch(forumEntrySubmissionRequested({ temporaryId, body: body.body, user }))

    try {
        const entry = await api.postForumEntry(body)

        dispatch(forumEntryCreated({ entry, temporaryId }))
        dispatch(deleteTemporaryEntry({ temporaryId }))
        getShowChannel(getState()).trigger(WEBSOCKET_FORUM_ENTRY, { entry, userId: user._id })
    } catch (err) {
        let status = _.get(err, 'response.data.statusCode')
        if (status >= 400 && status <= 499) {
            dispatch(deleteTemporaryEntry({ temporaryId }))
            let errorField = _.get(err, 'response.data.errors[0]')
            if (errorField && status === 429) {
                dispatch(reachedMaxAmountOfPosts())
                let msg = errorField[Object.keys(errorField)[0]] //Get the value of the first property in this obj
                dispatch(setInteractionError(msg))
            }
        }
    }
}

export const attemptToCreateFormEntry = (body, user) => (dispatch, getState) => {
    const { userCanPost } = getState().interaction

    if (userCanPost) {
        dispatch(createForumEntry(body, user))
    } else {
        dispatch(setInteractionError('Max number of entries reached'))
    }
}

export const sortForumAndRefresh = (showId, sort) => async (dispatch, getState) => {
    dispatch(setForumSort(sort))

    if (!getForum(getState()).length)
        dispatch(retrieveForum(showId, { count: FORUM.FORUM_MAX_ENTRIES_IN_MEMORY, sort }))
}

export const openChannel = channelName => async dispatch => {
    try {
        const wsChannel = await wsChannelProvider.openWsChannel(channelName)
        await dispatch(setShowChannelName(wsChannel.name))
        return wsChannel
    } catch (err) {
        dispatch(showErrorBanner({ message: "Uh oh! We're experiencing connectivity issues. Please refresh the page" }))
    }
}

export const getOpenChannel = channelName => async (dispatch, getState) => {
    let wsChannel = getShowChannel(getState())
    if (!wsChannel) {
        return await dispatch(openChannel(channelName))
    }
    return wsChannel
}

export const closeShowChannel = channelName => async dispatch => {
    wsChannelProvider.closeWsChannel(channelName)
    dispatch(setShowChannelName(null))
}

export const subscribeToShowChannel = show => async dispatch => {
    const wsChannel = await dispatch(getOpenChannel(show.channelName))

    wsChannel.bind(WEBSOCKET_VIEWER_COUNT_UPDATE, data => dispatch(viewerRegistered({ viewerCount: data.viewerCount })))
    wsChannel.bind(WEBSOCKET_VIDEO_STATUS_CHANGE, data => {
        if (data && data.status && data?.status === VideoStatus.ACTIVE) {
            dispatch(updateVideoStatus(data))
        }
    }) //Video idle listener
    wsChannel.bind(WEBSOCKET_REMAINING_PASS_UPDATE, data => dispatch(updateRemainingPassesAction(data)))
    wsChannel.bind(WEBSOCKET_INITIATE_PROTOCOL_ARMAGEDDON, data => dispatch(initiateProtocolArmageddon(data)))
    wsChannel.bind(WEBSOCKET_DEACTIVATE_PROTOCOL_ARMAGEDDON, () => dispatch(deactivateProtocolArmageddon()))

    wsChannel.bind(WEBSOCKET_FORCE_REFRESH, ({ url }) => (window.location.href = url))
    wsChannel.bind(WEBSOCKET_SET_ACTIVE_MUX_ASSET, ({ active }) => dispatch(updateActiveMuxStream(active)))

    return wsChannel
}

export const subscribeToForum = show => async dispatch => {
    const wsChannel = await dispatch(getOpenChannel(show.channelName))
    wsChannel.bind(WEBSOCKET_FORUM_TOP_ENTRIES_BATCH, batch => dispatch(forumTopEntriesBatch(batch)))
}
